<template>
  <div class="h-full">
    <form
      class="h-full flex flex-col"
      @submit.prevent="handleSubmit"
    >
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Optional donor search -->
        <div
          v-if="donorSearch"
          class="mb-4"
        >
          <label class="block text-sm font-medium text-gray-700">
            Search Existing Donors
          </label>
          <v-select
            ref="donorSelect"
            v-model="selectedDonor"
            :options="donorResults"
            :filterable="false"
            label="display"
            placeholder="Type to search existing donors..."
            @search="onDonorSearch"
            @update:model-value="onDonorSelected"
          >
            <template #no-options="{ search }">
              <span v-if="search.length">No donors found</span>
              <span v-else>Type to search...</span>
            </template>
            <template #list-footer>
              <li
                v-if="donorSearchTerm.length"
                class="px-3 py-2 border-t border-gray-200 text-sm text-black hover:bg-gray-100 cursor-pointer"
                @mousedown.prevent="addNewDonorFromSearch"
              >
                + Add "<strong>{{ donorSearchTerm }}</strong>" as a new donor
              </li>
            </template>
          </v-select>
          <p class="text-xs text-gray-400 mt-1">
            Or leave blank to create a new donor below
          </p>
        </div>

        <div
          v-for="(field, key) in tempRecord"
          :key="key"
          class="mb-4"
        >
          <label
            :for="key"
            class="block text-sm font-medium text-gray-700"
          >
            {{ t('forms.fields.' + key) }}
          </label>
          <input
            v-if="field.type === 'string'"
            :id="key"
            v-model="field.value"
            :placeholder="'Enter ' + t('forms.fields.' + key)"
            :required="isFieldRequired(field)"
            :disabled="isDonorField(key) && selectedDonor != null"
            :class="['mt-1 block w-full px-3 py-2 border shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md', errors[key] ? 'border-red-500' : 'border-gray-300', isDonorField(key) && selectedDonor ? 'bg-gray-100 cursor-not-allowed' : '']"
            @input="clearError(key)"
          >
          <v-select
            v-if="field.type === 'select'"
            :id="key"
            :key="field.options.length"
            v-model="field.value"
            :options="field.options"
            :required="isFieldRequired(field)"
            searchable
            placeholder="Select ..."
            append-to-body
          >
            <!--            <option-->
            <!--              value=""-->
            <!--              disabled-->
            <!--            >-->
            <!--              Select  {{ t('forms.fields.' + key) }}-->
            <!--            </option>-->
            <!--            <option-->
            <!--              v-for="option in field.options"-->
            <!--              :key="option"-->
            <!--              :value="option"-->
            <!--            >-->
            <!--              {{ option }}-->
            <!--            </option>-->
          </v-select>

          <currency-input
            v-if="field.type === 'money'"
            v-model="field.value"
            prefix="$ "
            :max="100000"
            :required="isFieldRequired(field)"
            :class="['mt-1 block w-full px-3 py-2 border shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md', errors[key] ? 'border-red-500' : 'border-gray-300']"
            @input="clearError(key)"
          />

          <vue-date-picker
            v-if="field.type === 'date'"
            id="donation-date"
            ref="donation-datepicker"
            v-model="field.value"
            :enable-time-picker="false"
            :placeholder="'Enter ' + t('forms.fields.' + key)"
            prevent-min-max-navigation
            auto-apply
            teleport
            class="max-w-[300px]"
          />

          <span
            v-if="errors[key]"
            class="text-red-500 text-sm mt-1 block"
          >{{ errors[key] }}</span>
        </div>
      </div>

      <button
        type="submit"
        class="shrink-0 w-full px-4 py-2 rounded button-filled text-default_white cursor-pointer mt-2"
      >
        Add to list
      </button>
    </form>
  </div>
</template>

<script>

import {defineComponent} from "vue";
import {useI18n} from "vue-i18n";
import CurrencyInput from "../currencyInput/currencyInput.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
// A Dynamic form to allow addition of records to the importer
export default defineComponent({
  name: "ExcelImportForm",
  components: {VueDatePicker, CurrencyInput, vSelect},
  props: {
    /**
     * PROP: MODEL
     * * This is a template of the record that gets created
     * The key should be the final key of the model's attribute
     * Keys should be added to i18n file for translation
     * Type controls what form field to display, only text and select currently
     * Value is the v-model of the corresponding input
     * * FORMAT
     * {
     *       "f_name": {
     *         type: "string",
     *         value: ""
     *       },
     *        "l_name": {
     *         type: "string",
     *         value: ""
     *       },
     *       "email": {
     *         type: "string",
     *         value: ""
     *       },
     *       "role": {
     *         type: "select",
     *         options: [
     *           "Owner", "Editor", "Manager",
     *         ],
     *         value: ""
     *       },
     *     }
     *
     * * OUTPUT
     * Outputs a formatted object with key-value pairs
     * {
     *   "f_name": "John",
     *   "l_name: "Doe",
     *   "email": "email@example.com"
     *   "role": "Editor"
     * }
     */
    model: {
      type: Object,
      required: true,
    },
    /**
     * PROP: donorSearch
     * Optional async function for searching existing donors.
     * Receives a search term string, returns a promise resolving to an array of
     * { id, f_name, l_name, email } objects.
     */
    donorSearch: {
      type: Function,
      default: null,
    },
  },
  emits: ['add-record'],

  setup() {
    // I18n
    const {t} = useI18n();

    return {t};
  },
  data() {
    return {
      tempRecord: {},
      errors: {},
      selectedDonor: null,
      donorResults: [],
      donorSearchTimeout: null,
      donorSearchTerm: '',
    };
  },
  mounted() {
    this.cloneTemplate();
  },
  methods: {
    // Makes a deep copy of the template model so we're not changing the store defaults
    cloneTemplate() {
      this.tempRecord = JSON.parse(JSON.stringify(this.model));
    },
    handleSubmit() {
      // Handle form submission
      let record = {};
      Object.keys(this.tempRecord).forEach((key) => {
        record[key] = this.tempRecord[key].value;
      });

      // Attach user_id if an existing donor was selected
      if (this.selectedDonor) {
        record.user_id = this.selectedDonor.id;
      }

      // Clear previous errors
      this.errors = {};

      // Validate email and amount
      if (record.email && !this.isValidEmail(record.email)) {
        this.errors.email = 'Please enter a valid email address';
      }

      if (record.hasOwnProperty('amount') && (!record.amount || record.amount <= 0)) {
        this.errors.amount = 'Please enter a valid amount greater than 0';
      }

      if (Object.keys(this.errors).length > 0) {
        return;
      }
      this.$emit('add-record', record);
      this.selectedDonor = null;
      this.donorResults = [];
      this.cloneTemplate();
    },

    isValidEmail(email) {
      return /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
    },

    // Checks if form field is required, defaults to true
    isFieldRequired(field) {
      if(field.hasOwnProperty('required')) {
        return field.required;
      }
      return true;
    },

    clearError(key) {
      if (this.errors[key]) {
        delete this.errors[key];
      }
    },

    isDonorField(key) {
      return ['f_name', 'l_name', 'email'].includes(key);
    },

    onDonorSearch(search) {
      this.donorSearchTerm = search;
      if (this.donorSearchTimeout) clearTimeout(this.donorSearchTimeout);
      if (!search.length) {
        this.donorResults = [];
        return;
      }
      this.donorSearchTimeout = setTimeout(async () => {
        const results = await this.donorSearch(search);
        this.donorResults = results.map(d => ({
          ...d,
          display: `${d.f_name || ''} ${d.l_name || ''} (${d.email})`.trim()
        }));
      }, 300);
    },

    addNewDonorFromSearch() {
      const term = this.donorSearchTerm.trim();
      const emailPattern = /^\S+@\S+\.\S+$/;

      // Clear all donor fields first to avoid stale data
      if (this.tempRecord.f_name) this.tempRecord.f_name.value = '';
      if (this.tempRecord.l_name) this.tempRecord.l_name.value = '';
      if (this.tempRecord.email) this.tempRecord.email.value = '';

      if (emailPattern.test(term)) {
        this.tempRecord.email.value = term;
      } else if (term.includes(' ')) {
        const spaceIndex = term.indexOf(' ');
        this.tempRecord.f_name.value = term.substring(0, spaceIndex);
        this.tempRecord.l_name.value = term.substring(spaceIndex + 1);
      } else {
        this.tempRecord.f_name.value = term;
      }

      this.selectedDonor = null;
      this.donorResults = [];
      this.donorSearchTerm = '';
      this.$refs.donorSelect.searchEl.blur();
    },

    onDonorSelected(donor) {
      if (donor) {
        if (this.tempRecord.f_name) this.tempRecord.f_name.value = donor.f_name || '';
        if (this.tempRecord.l_name) this.tempRecord.l_name.value = donor.l_name || '';
        if (this.tempRecord.email) this.tempRecord.email.value = donor.email || '';
      } else {
        if (this.tempRecord.f_name) this.tempRecord.f_name.value = '';
        if (this.tempRecord.l_name) this.tempRecord.l_name.value = '';
        if (this.tempRecord.email) this.tempRecord.email.value = '';
      }
    }
  }
});
</script>

<style lang="scss" scoped>
</style>
